import React, { useState, useEffect } from 'react';
import axios from 'axios';
import APIEndPoints from '../../../middleware/APIEndPoints';

const SatisfiedClients = () => {
  const cloudName = process.env.REACT_APP_CLOUD_NAME;
  const uploadPreset = 'upload';

  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ name: '', image: '', rating: 0, description: '' });
  const [editReview, setEditReview] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const baseUrl = APIEndPoints.client_reviews.url;

  // Fetch all reviews
  const fetchReviews = async () => {
    try {
      const response = await axios.get(baseUrl);
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Handle form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReview({ ...newReview, [name]: value });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewReview({ ...newReview, image: file });
    // Generate a preview of the image
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setImagePreview(previewURL);
    }
  };

  // Validate description length
  const validateDescription = (description) => {
    const wordCount = description.split(' ').length;
    return wordCount <= 15;
  };

  // Function to upload image to Cloudinary
  const handleUpload = async (imageFile) => {
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('upload_preset', uploadPreset);

    try {
      // Upload image to Cloudinary
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        formData
      );
      return response.data.secure_url; // Return the uploaded image URL
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  // Create a new review
  const handleCreate = async (e) => {
    e.preventDefault();

    if (!validateDescription(newReview.description)) {
      setErrorMessage('Description must not exceed 15 words.');
      return;
    }

    const imageUrl = await handleUpload(newReview.image);
    if (!imageUrl) {
      setErrorMessage('Image upload failed.');
      return;
    }

    try {
      const response = await axios.post(baseUrl, { ...newReview, image: imageUrl });
      setReviews([...reviews, response.data.review]);
      setNewReview({ name: '', image: '', rating: 0, description: '' });
      setImagePreview('');
      setErrorMessage('');
    } catch (error) {
      console.error('Error creating review:', error);
    }
  };

  // Delete a review
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${baseUrl}/${id}`);
      setReviews(reviews.filter(review => review._id !== id));
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  // Edit an existing review
  const handleEdit = (review) => {
    setEditReview(review);
    setImagePreview(review.image); // Set the preview to the existing image
  };

  // Update a review
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!validateDescription(editReview.description)) {
      setErrorMessage('Description must not exceed 15 words.');
      return;
    }

    let imageUrl = editReview.image;
    if (typeof editReview.image === 'object') {
      imageUrl = await handleUpload(editReview.image);
      if (!imageUrl) {
        setErrorMessage('Image upload failed.');
        return;
      }
    }

    try {
      const response = await axios.put(`${baseUrl}/${editReview._id}`, { ...editReview, image: imageUrl });
      setReviews(reviews.map(review => review._id === editReview._id ? response.data.review : review));
      setEditReview(null);
      setImagePreview('');
      setErrorMessage('');
    } catch (error) {
      console.error('Error updating review:', error);
    }
  };

  // Helper function to render stars based on the rating
  const renderStars = (rating, onClick) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        onClick={() => onClick(index + 1)} // Clickable star
        style={{ cursor: 'pointer', color: index < rating ? 'gold' : 'gray' }} // Style for filled and empty stars
      >
        ★
      </span>
    ));
  };

  // Handle star click for rating
  const handleStarClick = (rating) => {
    setNewReview({ ...newReview, rating });
  };

  const handleEditStarClick = (rating) => {
    setEditReview({ ...editReview, rating });
  };

  return (
    <div className="bg-[#1d0c39]  min-h-screen p-8 text-white">
      <h2 className="text-2xl font-bold mb-4">Satisfied Clients Reviews</h2>

      {/* Create new review */}
      <form onSubmit={handleCreate} className="mb-6">
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Name</label>
          <input type="text" name="name" value={newReview.name} onChange={handleInputChange} className="border p-2 rounded w-full text-black" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Image</label>
          <input type="file" onChange={handleFileChange} className="border p-2 rounded w-full" accept="image/*" required />
          {imagePreview && <img src={imagePreview} alt="Preview" className="mt-2 w-32 h-32 object-cover" />}
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Rating</label>
          <div className="flex mb-2">{renderStars(newReview.rating, handleStarClick)}</div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea name="description" value={newReview.description} onChange={handleInputChange} className="border p-2 rounded w-full text-black" rows="3" required></textarea>
        </div>
        {errorMessage && <p className="text-red-600 mb-4">{errorMessage}</p>}
        <button type="submit" className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 ">Create Review</button>
      </form>

      {/* Edit review form */}
      {editReview && (
        <form onSubmit={handleUpdate} className="mb-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Name</label>
            <input type="text" name="name" value={editReview.name} onChange={(e) => setEditReview({ ...editReview, name: e.target.value })} className="border p-2 rounded w-full" required />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Image</label>
            <input type="file" onChange={(e) => setEditReview({ ...editReview, image: e.target.files[0] })} className="border p-2 rounded w-full" accept="image/*" />
            {imagePreview && <img src={imagePreview} alt="Image Preview" className="mt-2 w-32 h-32 object-cover" />}
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Rating</label>
            <div className="flex mb-2">{renderStars(editReview.rating, handleEditStarClick)}</div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea name="description" value={editReview.description} onChange={(e) => setEditReview({ ...editReview, description: e.target.value })} className="border p-2 rounded w-full" rows="3" required></textarea>
          </div>
          {errorMessage && <p className="text-red-600 mb-4">{errorMessage}</p>}
          <button type="submit" className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">Update Review</button>
          <button type="button" onClick={() => setEditReview(null)} className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 ml-2">Cancel</button>
        </form>
      )}

      {/* Display reviews */}
      <div>
        {reviews.map((review) => (
          <div key={review._id} className="border p-4 mb-4 rounded">
            <h3 className="font-bold">{review.name}</h3>
            <div className="flex mb-2">{renderStars(review.rating)}</div> {/* Display stars */}
            <img src={review.image} alt={review.name} className="w-32 h-32 object-cover mt-2" />
            <p className="mt-2">{review.description}</p>
            <div className="flex justify-between mt-4">
              <button onClick={() => handleEdit(review)} className="bg-yellow-500 text-white py-1 px-3 rounded hover:bg-yellow-600">Edit</button>
              <button onClick={() => handleDelete(review._id)} className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SatisfiedClients;
